import { useState, useEffect, useCallback } from 'react';
import {
  ExternalLink,
  Link2,
  Link2Off,
  Loader2,
  X,
  AlertCircle,
  Check,
  ChevronRight,
  LayoutTemplate,
  FileText,
} from 'lucide-react';
import { getIntegrationsApi, type FigmaFrame, type FigmaFile } from '../../../lib/integrationsApi';
import { registerExternalId, getExternalIds } from '../../../lib/connectors';
import { getCurrentWorkspaceId, getSafeSupabaseClient } from '../../../lib/supabaseSync';

// ── Figma icon ────────────────────────────────────────────────────────────────

const FigmaIcon = ({ className = 'w-4 h-4' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none">
    <rect width="24" height="24" rx="4" fill="#1E1E1E" />
    <circle cx="10" cy="7" r="3" fill="#F24E1E" />
    <circle cx="14" cy="7" r="3" fill="#FF7262" />
    <circle cx="10" cy="12" r="3" fill="#A259FF" />
    <circle cx="14" cy="12" r="3" fill="#1ABCFE" />
    <circle cx="10" cy="17" r="3" fill="#0ACF83" />
  </svg>
);

// ── Types ─────────────────────────────────────────────────────────────────────

interface ExistingLink {
  connectorType: string;
  externalId: string;
  externalUrl: string | null;
  lastSyncedAt: string | null;
  mappingId?: string;
}

interface ItemFigmaSyncPanelProps {
  itemId: string;
  itemTitle?: string;
}

// ── Main component ────────────────────────────────────────────────────────────

export function ItemFigmaSyncPanel({ itemId, itemTitle }: ItemFigmaSyncPanelProps) {
  const workspaceId = getCurrentWorkspaceId();

  const [links, setLinks] = useState<ExistingLink[]>([]);
  const [linksLoading, setLinksLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [figmaConnected, setFigmaConnected] = useState<boolean | null>(null);

  // File browsing
  const [fileKey, setFileKey] = useState('');
  const [fileInfo, setFileInfo] = useState<FigmaFile | null>(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  // Frame browsing
  const [frames, setFrames] = useState<FigmaFrame[]>([]);
  const [framesLoading] = useState(false);
  const [selectedFrame, setSelectedFrame] = useState<FigmaFrame | null>(null);

  // Save
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLinks = useCallback(async () => {
    if (!workspaceId) return;
    setLinksLoading(true);
    try {
      const allLinks = await getExternalIds(workspaceId, 'item', itemId);
      const fLinks = allLinks.filter((l) => l.connectorType === 'figma');

      const supabase = getSafeSupabaseClient();
      if (supabase && fLinks.length > 0) {
        const { data } = await supabase
          .from('entity_external_ids')
          .select('id, external_id')
          .eq('workspace_id', workspaceId)
          .eq('entity_type', 'item')
          .eq('internal_id', itemId)
          .eq('connector_type', 'figma')
          .eq('is_active', true);

        const idMap: Record<string, string> = {};
        for (const row of data ?? []) {
          idMap[row.external_id as string] = row.id as string;
        }
        setLinks(fLinks.map((l) => ({ ...l, mappingId: idMap[l.externalId] })));
      } else {
        setLinks(fLinks);
      }
    } finally {
      setLinksLoading(false);
    }
  }, [workspaceId, itemId]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  // Check connection when panel opens
  useEffect(() => {
    if (!isOpen || !workspaceId) return;
    getIntegrationsApi(workspaceId)
      .getFigmaStatus()
      .then((s) => setFigmaConnected(s.connected))
      .catch(() => setFigmaConnected(false));
  }, [isOpen, workspaceId]);

  const handleOpen = () => {
    setIsOpen(true);
    setFileKey('');
    setFileInfo(null);
    setFrames([]);
    setSelectedFrame(null);
    setError(null);
    setSaveSuccess(false);
    setFileError(null);
  };

  const handleClose = () => {
    setIsOpen(false);
    setFileKey('');
    setFileInfo(null);
    setFrames([]);
    setSelectedFrame(null);
    setError(null);
    setFileError(null);
  };

  // Parse file key from a Figma URL or raw key
  const parseFileKey = (input: string): string => {
    const match = input.match(/figma\.com\/(?:file|design)\/([A-Za-z0-9]+)/);
    return match ? match[1] : input.trim();
  };

  const handleLoadFile = async () => {
    if (!workspaceId || !fileKey.trim()) return;
    const key = parseFileKey(fileKey);
    setFileLoading(true);
    setFileError(null);
    setFrames([]);
    setSelectedFrame(null);
    setFileInfo(null);
    try {
      const api = getIntegrationsApi(workspaceId);
      const [file, frameList] = await Promise.all([
        api.getFigmaFile(key),
        api.listFigmaFrames(key),
      ]);
      setFileInfo(file);
      setFrames(frameList);
    } catch (err) {
      setFileError(err instanceof Error ? err.message : 'Failed to load file');
    } finally {
      setFileLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedFrame || !workspaceId) return;
    setSaving(true);
    setError(null);
    try {
      await registerExternalId({
        workspaceId,
        entityType: 'item',
        internalId: itemId,
        connectorType: 'figma',
        externalId: selectedFrame.id,
        externalUrl: selectedFrame.url,
      });
      setSaveSuccess(true);
      await loadLinks();
      setTimeout(() => {
        setSaveSuccess(false);
        handleClose();
      }, 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save link');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async (mappingId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    await supabase
      .from('entity_external_ids')
      .update({ is_active: false })
      .eq('id', mappingId);
    await loadLinks();
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="pt-3 border-t border-[var(--rule)]">
      {/* Section header */}
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide flex items-center gap-1.5">
          <FigmaIcon className="w-3.5 h-3.5" />
          Figma Frame
        </p>
        {!linksLoading && (
          <button
            onClick={handleOpen}
            className="flex items-center gap-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] transition-colors"
          >
            <Link2 className="h-3 w-3" />
            {links.length > 0 ? 'Add another' : 'Link to frame'}
          </button>
        )}
      </div>

      {/* Existing links */}
      {linksLoading ? (
        <div className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] py-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading…
        </div>
      ) : links.length === 0 ? (
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] italic py-0.5">
          Not linked to a Figma frame
        </p>
      ) : (
        <div className="space-y-1.5">
          {links.map((link) => (
            <div
              key={link.externalId}
              className="flex items-center gap-2 group px-2 py-1.5 bg-[var(--surface)] dark:bg-[var(--bg-raised)]/40 border border-[var(--rule)]"
            >
              <LayoutTemplate className="h-3.5 w-3.5 text-[#A259FF] flex-shrink-0" />
              <span className="text-xs font-mono font-medium text-[var(--text-sec)] truncate flex-1">
                {link.externalId}
              </span>
              {link.externalUrl && (
                <a
                  href={link.externalUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[var(--text-ter)] hover:text-[#1ABCFE] transition-colors flex-shrink-0"
                  title="Open in Figma"
                >
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {link.mappingId && (
                <button
                  onClick={() => handleUnlink(link.mappingId!)}
                  className="text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                  title="Remove link"
                >
                  <Link2Off className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Inline link panel */}
      {isOpen && (
        <div className="mt-3 border border-[#1E1E1E]/20 bg-[var(--bg-raised)] dark:bg-[var(--surface)] shadow-sm overflow-hidden">
          {/* Panel header */}
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)]/50">
            <span className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] flex items-center gap-1.5">
              <Link2 className="h-3.5 w-3.5 text-[#1ABCFE]" />
              Link to Figma frame
            </span>
            <button
              onClick={handleClose}
              className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="p-3 space-y-3">
            {/* Not connected */}
            {figmaConnected === false && (
              <div className="flex items-start gap-2 p-2.5 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border border-[var(--mustard)] dark:border-[var(--mustard)]">
                <AlertCircle className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
                  Figma is not connected. Go to{' '}
                  <a href="/prioritization/settings/connectors" className="underline font-medium">
                    Settings → Connectors
                  </a>{' '}
                  to connect your Figma account.
                </p>
              </div>
            )}

            {figmaConnected !== false && (
              <>
                {/* File URL / key input */}
                <div>
                  <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                    Figma file URL or key
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={fileKey}
                      onChange={(e) => setFileKey(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleLoadFile()}
                      placeholder={itemTitle ? `e.g. figma.com/design/…` : 'Paste Figma file URL or key'}
                      className="flex-1 px-2.5 py-1.5 text-xs border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:border-[#1ABCFE] focus:ring-2 focus:ring-[#1ABCFE]/20"
                    />
                    <button
                      type="button"
                      onClick={handleLoadFile}
                      disabled={!fileKey.trim() || fileLoading}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-[var(--bg)] text-white hover:bg-[var(--bg-raised)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      {fileLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ChevronRight className="h-3 w-3" />}
                      Load
                    </button>
                  </div>
                  {fileError && (
                    <p className="mt-1 text-xs text-[var(--coral)]">{fileError}</p>
                  )}
                </div>

                {/* File info */}
                {fileInfo && (
                  <div className="flex items-center gap-2 px-2 py-1.5 bg-[var(--surface)] dark:bg-[var(--bg-raised)]/40 border border-[var(--rule)]">
                    <FileText className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                    <span className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] font-medium truncate">{fileInfo.name}</span>
                    <span className="text-xs text-[var(--text-ter)] flex-shrink-0">{frames.length} frame{frames.length !== 1 ? 's' : ''}</span>
                  </div>
                )}

                {/* Frames list */}
                {framesLoading && (
                  <div className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] py-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading frames…
                  </div>
                )}

                {frames.length > 0 && (
                  <div className="border border-[var(--rule)] overflow-hidden max-h-52 overflow-y-auto">
                    {frames.map((frame) => {
                      const isSelected = selectedFrame?.id === frame.id;
                      return (
                        <button
                          key={frame.id}
                          type="button"
                          onClick={() => setSelectedFrame(isSelected ? null : frame)}
                          className={`w-full flex items-start gap-2 px-3 py-2 text-left text-xs border-b border-[var(--rule)] last:border-0 transition-colors ${
                            isSelected
                              ? 'bg-[#1ABCFE]/10 dark:bg-[#1ABCFE]/5'
                              : 'hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/30'
                          }`}
                        >
                          <LayoutTemplate className="h-3.5 w-3.5 text-[#A259FF] flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[var(--text-sec)] dark:text-[var(--text-sec)] truncate font-medium leading-tight">
                              {frame.name}
                            </p>
                            <p className="text-[var(--text-ter)] mt-0.5 truncate leading-tight">
                              {frame.pageName}
                            </p>
                          </div>
                          {isSelected && (
                            <Check className="h-4 w-4 text-[#1ABCFE] flex-shrink-0 mt-0.5" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {fileInfo && frames.length === 0 && !framesLoading && (
                  <p className="text-xs text-[var(--text-ter)] italic text-center py-2">
                    No top-level frames found in this file
                  </p>
                )}

                {/* Error */}
                {error && (
                  <div className="flex items-start gap-1.5 text-xs text-[var(--coral)] dark:text-[var(--coral)] p-2 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20">
                    <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    {error}
                  </div>
                )}

                {/* Save / Cancel */}
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-3 py-1.5 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!selectedFrame || saving || saveSuccess}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-[var(--bg)] hover:bg-[var(--bg-raised)] text-white"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Saving…
                      </>
                    ) : saveSuccess ? (
                      <>
                        <Check className="h-3 w-3" />
                        Linked!
                      </>
                    ) : (
                      <>
                        <Link2 className="h-3 w-3" />
                        Link Frame
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
